/**
 * GroupListView — Telegram-grade list of topics inside an auto-spawn chat.
 *
 * Sections (collapsible):
 *   - Active   (default-open + not snoozed) — sorted by last_message_ts DESC
 *   - Snoozed  (default-collapsed + snoozed_until > now AND not closed) — sorted by wake_at ASC
 *   - Done     (closed) — sorted by closed_at DESC; default-collapsed
 *
 * Wired to ChatPage via the `?topic_id=…` URL parameter:
 *   - clicking a row sets ?topic_id=<id> → ChatPage swaps to MessageList view
 *   - back button or "Back to group" link clears ?topic_id
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Virtuoso } from 'react-virtuoso'
import { clsx } from 'clsx'
import { CheckSquare, ChevronDown, ChevronRight, Clock, ExternalLink, ListTree, MailOpen, MoreVertical, Plus, Inbox, PencilLine, X } from 'lucide-react'
import {
  useTopics, useCreateTopic, useCloseTopic, useCloseTopics, useBlockTopicSource, useReopenTopic, useSnoozeTopic, usePrefetchChat, useMarkChatRead, type Topic, type Chat,
} from '../lib/api'
import { draftTargetKey, isVisibleDraft, useUi } from '../store/ui'
import { TODO_CHAT_ID } from '../lib/chatNavigation'
import { outboxTopicKey, useOutboxPendingScopes } from '../lib/outboxPending'
import { discardPendingShare, peekPendingShare } from '../lib/pendingShares'
import { preloadThreadViewComponents } from '../lib/preloadChatViews'
import { openPublicDashboard } from '../lib/publicDashboards'
import { clearReadNotificationsForChat } from '../lib/notificationClear'
import { IncludeDoneCheckbox } from './SearchFilterBar'
import GroupTopicRow from './GroupTopicRow'
import NewTopicPromptDialog from './NewTopicPromptDialog'
import SnoozePicker from './SnoozePicker'
import UndoToast from './UndoToast'
import {
  classifyTopic as classify,
  countOpenXLinkSourceTopics,
  countExpandedTopicRows,
  countTopicFiltersForExpandedSections,
  nextTopicWorkStatusFlags,
  saveTopicAdvanceContext,
  topicMatchesWorkStatusFilter,
  topicMatchesXLinkSourceMode,
  topicWorkStatusFilterFromFlags,
  type XLinkSourceMode,
} from '../lib/topicFilterCounts'
import {
  compareTodoistInboxOrder,
  groupTodoistTopicsBySection,
  todoistRowHierarchy,
  todoistTaskId,
  visibleTodoistTopicsAfterCollapse,
} from '../lib/todoistTopicOrdering'
import { confirmTodoistClose, isTodoistSourceKind } from '../lib/todoistCloseAlert'

type TopicFilters = {
  unreadOnly: boolean
  repliedOnly: boolean
  finishedOnly: boolean
  runningOnly: boolean
  xLinkSourceMode: XLinkSourceMode
}

type Props = {
  chat: Chat
  onOpenTopic: (topicId: string, options?: { focusComposer?: boolean }) => void
  searchQuery: string
  searchOpen?: boolean
  newTopicShareId?: number | null
  newTopicPrompt?: string
  onNewTopicShareConsumed?: () => void
}

type SectionKey = 'drafts' | 'active' | 'snoozed' | 'done'
type TopicSortMode = 'current' | 'todoist'

type OpenSections = Record<SectionKey, boolean>

type TopicListItem =
  | {
      kind: 'section'
      section: SectionKey
      title: string
      count: number
      tone: 'default' | 'amber' | 'muted' | 'draft'
      icon?: React.ReactNode
    }
  | { kind: 'todoist-section'; name: string; count: number }
  | { kind: 'topic'; topic: Topic; section: 'active' | 'snoozed' | 'done' }
  | { kind: 'empty-active' }
  | { kind: 'empty-search' }
  | { kind: 'older-done'; loading: boolean }

const DEFAULT_TOPIC_FILTERS: TopicFilters = {
  unreadOnly: false,
  repliedOnly: false,
  finishedOnly: false,
  runningOnly: false,
  xLinkSourceMode: 'both',
}

const TOPIC_FILTERS_STORAGE_PREFIX = 'lane-control:group-topic-filters'
const TOPIC_SORT_STORAGE_PREFIX = 'lane-control:group-topic-sort'
const DIRECT_TOPIC_RENDER_LIMIT = 80
const FIRST_FRAME_TOPIC_ROWS = 32
const X_LINK_SOURCE_MODE_ORDER: XLinkSourceMode[] = ['xlinks', 'bookmarks', 'both']
const COMMITMENTS_CHAT_ID = 'pwa-auto-commitments'
const EVENTS_CHAT_ID = 'pwa-auto-event-tickets'
const ARCHITECTURE_DEEPEN_CHAT_ID = 'pwa-auto-architecture-deepen'
const DEFAULT_OPEN_SECTIONS: OpenSections = {
  drafts: true,
  active: true,
  snoozed: false,
  done: false,
}

function topicFiltersStorageKey(chatId: string): string {
  return `${TOPIC_FILTERS_STORAGE_PREFIX}:${chatId}`
}

function topicSortStorageKey(chatId: string): string {
  return `${TOPIC_SORT_STORAGE_PREFIX}:${chatId}`
}

function loadTopicSortMode(chatId: string): TopicSortMode {
  try {
    const saved = localStorage.getItem(topicSortStorageKey(chatId))
    if (saved === 'current' || saved === 'todoist') return saved
    return chatId === TODO_CHAT_ID ? 'todoist' : 'current'
  } catch {
    return chatId === TODO_CHAT_ID ? 'todoist' : 'current'
  }
}

function saveTopicSortMode(chatId: string, mode: TopicSortMode): void {
  try {
    localStorage.setItem(topicSortStorageKey(chatId), mode)
  } catch {/* ignore */}
}

function loadTopicFilters(chatId: string): TopicFilters {
  try {
    const raw = localStorage.getItem(topicFiltersStorageKey(chatId))
    if (!raw) return DEFAULT_TOPIC_FILTERS
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const finishedOnly = parsed.finishedOnly === true
    const parsedSourceMode = parsed.xLinkSourceMode
    const xLinkSourceMode: XLinkSourceMode =
      parsedSourceMode === 'xlinks' || parsedSourceMode === 'bookmarks' || parsedSourceMode === 'both'
        ? parsedSourceMode
        : parsed.todoistOnly === true
          ? 'xlinks'
          : 'both'
    return {
      unreadOnly: parsed.unreadOnly === true,
      repliedOnly: parsed.repliedOnly === true,
      finishedOnly,
      runningOnly: !finishedOnly && parsed.runningOnly === true,
      xLinkSourceMode,
    }
  } catch {
    return DEFAULT_TOPIC_FILTERS
  }
}

function isCommitmentsDashboardTopic(chatId: string, topic: Topic): boolean {
  return (
    chatId === COMMITMENTS_CHAT_ID
    && (
      topic.source_kind === 'commitment_board'
      || (topic.title || '').trim() === 'Commitments — status board'
    )
  )
}

function saveTopicFilters(chatId: string, filters: TopicFilters): void {
  try {
    localStorage.setItem(topicFiltersStorageKey(chatId), JSON.stringify(filters))
  } catch {/* ignore */}
}

function filterButtonClass(active: boolean, activeClass: string): string {
  return clsx(
    'inline-flex h-10 min-w-0 items-center justify-center gap-1 rounded-lg px-1.5',
    'text-[13px] font-medium leading-none whitespace-nowrap transition-colors',
    active
      ? activeClass
      : 'text-stone-600 dark:text-stone-300 hover:bg-stone-200 dark:hover:bg-stone-700',
  )
}

export default function GroupListView({
  chat,
  onOpenTopic,
  searchQuery,
  searchOpen = false,
  newTopicShareId = null,
  newTopicPrompt = '',
  onNewTopicShareConsumed,
}: Props) {
  const navigate = useNavigate()
  const [showOlderDone, setShowOlderDone] = useState(false)
  const [includeDone, setIncludeDone] = useState(false)
  const includeOldDone = showOlderDone || (includeDone && searchQuery.trim().length > 0)
  useEffect(() => { setIncludeDone(false) }, [chat.id, searchOpen])
  const { data: topics, isLoading, isFetching, error } = useTopics(chat.id, { includeOldDone })
  const draftsByChat = useUi((s) => s.draftsByChat)
  const draftUpdatedAt = useUi((s) => s.draftUpdatedAt)
  const createTopic = useCreateTopic(chat.id)
  const closeTopic = useCloseTopic(chat.id)
  const closeTopics = useCloseTopics(chat.id)
  const markChatRead = useMarkChatRead(chat.id)
  const blockTopicSource = useBlockTopicSource(chat.id)
  const reopenTopic = useReopenTopic(chat.id)
  const snoozeTopic = useSnoozeTopic(chat.id)
  const prefetchChat = usePrefetchChat()
  // Undelivered outbox work per topic — a topic holding one is running,
  // never finished, even though the server sees no queued row for it yet.
  const { topicKeys: outboxTopicKeys } = useOutboxPendingScopes()
  const topicHasPendingOutbox = useCallback(
    (topic: Pick<Topic, 'id'>) => outboxTopicKeys.has(outboxTopicKey(chat.id, topic.id)),
    [chat.id, outboxTopicKeys],
  )

  const [snoozeFor, setSnoozeFor] = useState<string | null>(null)
  const [undoToast, setUndoToast] = useState<{
    topicIds: string[]
    message: string
    reopenTodoist: boolean
  } | null>(null)
  const [topicActionError, setTopicActionError] = useState<string | null>(null)
  const [topicFilters, setTopicFilters] = useState(() => loadTopicFilters(chat.id))
  const [selectedTopicIds, setSelectedTopicIds] = useState<Set<string>>(() => new Set())
  const [openSections, setOpenSections] = useState<OpenSections>(() => ({ ...DEFAULT_OPEN_SECTIONS }))
  const [controlsReady, setControlsReady] = useState(false)
  const [virtualListReady, setVirtualListReady] = useState(false)
  const [topicMenuOpen, setTopicMenuOpen] = useState(false)
  const [newTopicOpen, setNewTopicOpen] = useState(false)
  const [newTopicInitialPrompt, setNewTopicInitialPrompt] = useState('')
  const [newTopicFocusComposerOnCreate, setNewTopicFocusComposerOnCreate] = useState(false)
  const [newTopicError, setNewTopicError] = useState<string | null>(null)
  const [topicSortMode, setTopicSortMode] = useState<TopicSortMode>(() => loadTopicSortMode(chat.id))
  const [collapsedTodoistSections, setCollapsedTodoistSections] = useState<Set<string>>(() => new Set())
  const [collapsedTodoistTaskIds, setCollapsedTodoistTaskIds] = useState<Set<string>>(() => new Set())
  const { unreadOnly, repliedOnly, finishedOnly, runningOnly, xLinkSourceMode } = topicFilters
  const isXLinksGroup = chat.id === 'pwa-auto-x-links' || chat.category === 'x-links'
  const isTodoistTasksGroup = chat.id === TODO_CHAT_ID
  const topicSortIsTodoist = isTodoistTasksGroup && topicSortMode === 'todoist'
  const isCommitmentsGroup = chat.id === COMMITMENTS_CHAT_ID
  const isEventsGroup = chat.id === EVENTS_CHAT_ID || chat.category === 'event-tickets'
  const isArchitectureDeepenGroup = chat.id === ARCHITECTURE_DEEPEN_CHAT_ID
  const selectionMode = selectedTopicIds.size > 0
  const dashboardTopic = useMemo(
    () => (topics || []).find((topic) => isCommitmentsDashboardTopic(chat.id, topic)) ?? null,
    [chat.id, topics],
  )
  const topicRows = useMemo(
    () => (topics || []).filter((topic) => !isCommitmentsDashboardTopic(chat.id, topic)),
    [chat.id, topics],
  )

  useEffect(() => {
    if (isCommitmentsGroup) void import('../pages/CommitmentsPage')
    if (isEventsGroup) void import('../pages/EventsPage')
  }, [isCommitmentsGroup, isEventsGroup])

  useEffect(() => {
    setTopicFilters(loadTopicFilters(chat.id))
    setTopicSortMode(loadTopicSortMode(chat.id))
    setCollapsedTodoistTaskIds(new Set())
    setCollapsedTodoistSections(new Set())
    setSelectedTopicIds(new Set())
    setOpenSections({ ...DEFAULT_OPEN_SECTIONS })
    setShowOlderDone(false)
    setControlsReady(false)
    setTopicMenuOpen(false)
    setNewTopicOpen(false)
    setNewTopicError(null)
    const id = window.requestAnimationFrame(() => setControlsReady(true))
    return () => window.cancelAnimationFrame(id)
  }, [chat.id])

  useEffect(() => {
    if (!topics || closeTopics.isPending) return
    const openTopicIds = new Set(topicRows.filter((topic) => !topic.closed).map((topic) => topic.id))
    setSelectedTopicIds((current) => {
      const next = new Set([...current].filter((topicId) => openTopicIds.has(topicId)))
      return next.size === current.size ? current : next
    })
  }, [topicRows, topics, closeTopics.isPending])

  const cycleXLinkSourceMode = useCallback((): void => {
    setTopicFilters((current) => {
      const index = X_LINK_SOURCE_MODE_ORDER.indexOf(current.xLinkSourceMode)
      const xLinkSourceMode = X_LINK_SOURCE_MODE_ORDER[(index + 1) % X_LINK_SOURCE_MODE_ORDER.length]
      const next = { ...current, xLinkSourceMode }
      saveTopicFilters(chat.id, next)
      return next
    })
  }, [chat.id])

  const toggleTopicSortMode = useCallback((): void => {
    setTopicSortMode((current) => {
      const next = current === 'todoist' ? 'current' : 'todoist'
      saveTopicSortMode(chat.id, next)
      return next
    })
  }, [chat.id])

  const toggleTodoistSection = useCallback((name: string): void => {
    setCollapsedTodoistSections((current) => {
      const next = new Set(current)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }, [])

  const toggleTodoistTaskCollapsed = useCallback((taskId: string): void => {
    setCollapsedTodoistTaskIds((current) => {
      const next = new Set(current)
      if (next.has(taskId)) next.delete(taskId)
      else next.add(taskId)
      return next
    })
  }, [])

  const toggleTopicFilter = useCallback((filter: Exclude<keyof TopicFilters, 'xLinkSourceMode'>): void => {
    setTopicFilters((current) => {
      if (filter === 'finishedOnly' || filter === 'runningOnly') {
        const next = { ...current, ...nextTopicWorkStatusFlags(current, filter) }
        saveTopicFilters(chat.id, next)
        return next
      }

      const next = { ...current, [filter]: !current[filter] }
      saveTopicFilters(chat.id, next)
      return next
    })
  }, [chat.id])

  const topicDraftAt = useCallback((topic: Topic): number => {
    const key = draftTargetKey(chat.id, topic.id)
    return isVisibleDraft(draftsByChat[key]) ? (draftUpdatedAt[key] || 1) : 0
  }, [chat.id, draftsByChat, draftUpdatedAt])

  const toggle = useCallback((section: SectionKey) => {
    setOpenSections((s) => ({ ...s, [section]: !s[section] }))
  }, [])

  const markDone = useCallback(async (topic: Topic): Promise<boolean> => {
    setTopicActionError(null)
    try {
      if (isTodoistSourceKind(topic.source_kind)) {
        const confirmed = await confirmTodoistClose({ topicIds: [topic.id] })
        if (!confirmed) return false
      }
      await closeTopic.mutateAsync({ topic_id: topic.id, closeTodoist: true })
      setUndoToast({
        topicIds: [topic.id],
        message: `"${topic.title || '(untitled)'}" marked done`,
        reopenTodoist: true,
      })
      return true
    } catch (error) {
      setTopicActionError(error instanceof Error
        ? error.message
        : `Could not complete "${topic.title || '(untitled)'}" in Todoist. It remains active.`)
      return false
    }
  }, [closeTopic])

  const reopenDone = useCallback((topic: Topic) => {
    setTopicActionError(null)
    reopenTopic.mutate({
      topic_id: topic.id,
      reopenTodoist: chat.id === 'pwa-auto-todoist-tasks',
    }, {
      onError: () => setTopicActionError(
        `Could not reopen "${topic.title || '(untitled)'}". It remains done.`,
      ),
    })
  }, [chat.id, reopenTopic])

  const blockSource = useCallback((topic: Topic) => {
    blockTopicSource.mutate({ topic_id: topic.id })
    setUndoToast({
      topicIds: [topic.id],
      message: `"${topic.title || '(untitled)'}" hidden from AgentChat`,
      reopenTodoist: false,
    })
  }, [blockTopicSource])

  const selectTopic = useCallback((topic: Topic) => {
    if (topic.closed) return
    setSelectedTopicIds((current) => {
      if (current.has(topic.id)) return current
      const next = new Set(current)
      next.add(topic.id)
      return next
    })
  }, [])

  const toggleSelectedTopic = useCallback((topic: Topic) => {
    if (topic.closed) return
    setSelectedTopicIds((current) => {
      const next = new Set(current)
      if (next.has(topic.id)) next.delete(topic.id)
      else next.add(topic.id)
      return next
    })
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedTopicIds(new Set())
  }, [])

  const closeSelectedTopics = useCallback(async () => {
    const topicIds = [...selectedTopicIds]
    if (topicIds.length === 0 || closeTopics.isPending) return
    setTopicActionError(null)
    try {
      const todoistTopicIds = topicRows
        .filter((topic) => selectedTopicIds.has(topic.id) && isTodoistSourceKind(topic.source_kind))
        .map((topic) => topic.id)
      if (todoistTopicIds.length > 0) {
        const confirmed = await confirmTodoistClose({ topicIds: todoistTopicIds })
        if (!confirmed) return
      }
      await closeTopics.mutateAsync({ topic_ids: topicIds, closeTodoist: true })
      setUndoToast({
        topicIds,
        message: `${topicIds.length} ${topicIds.length === 1 ? 'topic' : 'topics'} marked done`,
        reopenTodoist: true,
      })
      clearSelection()
    } catch (error) {
      setTopicActionError(error instanceof Error
        ? error.message
        : 'Could not complete the selected Todoist tasks. They remain active.')
    }
  }, [clearSelection, closeTopics, selectedTopicIds, topicRows])

  const markReadSelectedTopics = useCallback(async () => {
    const topicIds = [...selectedTopicIds]
    if (topicIds.length === 0 || markChatRead.isPending) return
    await Promise.all(topicIds.map((topicId) => markChatRead.mutateAsync({ topicId })))
    for (const topicId of topicIds) {
      clearReadNotificationsForChat(chat.id, topicId, chat.display_name ?? null)
    }
    clearSelection()
  }, [chat.display_name, chat.id, clearSelection, markChatRead, selectedTopicIds])

  const openNewTopicDialog = useCallback(() => {
    setTopicMenuOpen(false)
    setNewTopicError(null)
    setNewTopicInitialPrompt('')
    setNewTopicFocusComposerOnCreate(false)
    setNewTopicOpen(true)
  }, [])

  useEffect(() => {
    if (!isXLinksGroup || !newTopicShareId) return
    let cancelled = false
    void peekPendingShare(newTopicShareId).then((share) => {
      if (cancelled) return
      const openingPrompt = share?.text.trim() || ''
      if (openingPrompt) {
        setTopicMenuOpen(false)
        setNewTopicError(null)
        setNewTopicInitialPrompt(openingPrompt)
        setNewTopicFocusComposerOnCreate(true)
        setNewTopicOpen(true)
      }
      void discardPendingShare(newTopicShareId)
      onNewTopicShareConsumed?.()
    })
    return () => { cancelled = true }
  }, [isXLinksGroup, newTopicShareId, onNewTopicShareConsumed])

  useEffect(() => {
    const openingPrompt = newTopicPrompt.trim()
    if (!isXLinksGroup || !openingPrompt) return
    setTopicMenuOpen(false)
    setNewTopicError(null)
    setNewTopicInitialPrompt(openingPrompt)
    setNewTopicFocusComposerOnCreate(true)
    setNewTopicOpen(true)
    onNewTopicShareConsumed?.()
  }, [isXLinksGroup, newTopicPrompt, onNewTopicShareConsumed])

  const createTopicFromPrompt = useCallback(async (openingPrompt: string) => {
    setNewTopicError(null)
    try {
      const created = await createTopic.mutateAsync({ opening_prompt: openingPrompt })
      const focusComposer = newTopicFocusComposerOnCreate
      setNewTopicOpen(false)
      setNewTopicInitialPrompt('')
      setNewTopicFocusComposerOnCreate(false)
      onOpenTopic(created.id, { focusComposer })
    } catch (e) {
      setNewTopicError(e instanceof Error ? e.message : 'Topic creation failed')
    }
  }, [createTopic, newTopicFocusComposerOnCreate, onOpenTopic])

  // Filter before sectioning so the section counts reflect what's visible.
  // Drafts survive the read/reply filters, but not the work-state filters.
  const nonStatusBaseVisible = useMemo(() => topicRows.filter((t) => {
    if (topicDraftAt(t) > 0) return true
    if (unreadOnly && (t.unread ?? 0) === 0) return false
    if (repliedOnly && (t.inbound_count ?? 0) === 0) return false
    return true
  }), [topicRows, topicDraftAt, unreadOnly, repliedOnly])

  const workStatusFilter = topicWorkStatusFilterFromFlags(finishedOnly, runningOnly)
  const sourceModeBaseVisible = useMemo(
    () => nonStatusBaseVisible.filter((t) => topicMatchesWorkStatusFilter(t, workStatusFilter, topicHasPendingOutbox(t))),
    [nonStatusBaseVisible, topicHasPendingOutbox, workStatusFilter],
  )

  const visible = useMemo(() => sourceModeBaseVisible.filter((t) => (
    !isXLinksGroup || topicMatchesXLinkSourceMode(t, xLinkSourceMode)
  )), [sourceModeBaseVisible, xLinkSourceMode, isXLinksGroup])

  const statusFilterCountRows = useMemo(() => nonStatusBaseVisible.filter((t) => (
    !isXLinksGroup || topicMatchesXLinkSourceMode(t, xLinkSourceMode)
  )), [nonStatusBaseVisible, xLinkSourceMode, isXLinksGroup])

  const todoistSections = useMemo(() => topicSortIsTodoist ? groupTodoistTopicsBySection(
    visible.filter((topic) => !topic.closed), collapsedTodoistTaskIds,
  ) : [], [visible, collapsedTodoistTaskIds, topicSortIsTodoist])

  const todoistHierarchyByTopicId = useMemo(() => topicSortIsTodoist
    ? new Map([
      ...todoistRowHierarchy(visible.filter((topic) => !topic.closed)),
      ...todoistRowHierarchy(visible.filter((topic) => topic.closed)),
    ])
    : new Map(), [visible, topicSortIsTodoist])

  const expandedTodoistTopics = useMemo(() => todoistSections
    .filter((section) => unreadOnly || !collapsedTodoistSections.has(section.name))
    .flatMap((section) => section.topics), [todoistSections, collapsedTodoistSections, unreadOnly])

  const sorted = useMemo(() => {
    const drafts: Topic[] = []
    const active: Topic[] = []
    const snoozed: Topic[] = []
    const done: Topic[] = []
    for (const t of visible) {
      if (topicDraftAt(t) > 0) {
        drafts.push(t)
        continue
      }
      const k = classify(t)
      if (k === 'active') active.push(t)
      else if (k === 'snoozed') snoozed.push(t)
      else done.push(t)
    }
    const currentActiveCompare = (a: Topic, b: Topic) => (b.last_message_ts || b.created_at) - (a.last_message_ts || a.created_at)
    const currentSnoozedCompare = (a: Topic, b: Topic) => (a.snoozed_until || 0) - (b.snoozed_until || 0)
    const currentDoneCompare = (a: Topic, b: Topic) => (b.closed_at || 0) - (a.closed_at || 0)
    const useTodoistOrder = isTodoistTasksGroup && topicSortMode === 'todoist'

    drafts.sort((a, b) => topicDraftAt(b) - topicDraftAt(a))
    active.sort(useTodoistOrder
      ? (a, b) => compareTodoistInboxOrder(a, b, currentActiveCompare)
      : currentActiveCompare)
    snoozed.sort(useTodoistOrder
      ? (a, b) => compareTodoistInboxOrder(a, b, currentSnoozedCompare)
      : currentSnoozedCompare)
    done.sort(useTodoistOrder
      ? (a, b) => compareTodoistInboxOrder(a, b, currentDoneCompare)
      : currentDoneCompare)

    if (!useTodoistOrder) return { drafts, active, snoozed, done }
    return {
      drafts,
      active: visibleTodoistTopicsAfterCollapse(active, collapsedTodoistTaskIds),
      snoozed: visibleTodoistTopicsAfterCollapse(snoozed, collapsedTodoistTaskIds),
      done: visibleTodoistTopicsAfterCollapse(done, collapsedTodoistTaskIds),
    }
  }, [collapsedTodoistTaskIds, isTodoistTasksGroup, topicDraftAt, topicSortMode, visible])

  const filterCounts = useMemo(
    () => controlsReady
      ? countTopicFiltersForExpandedSections(
        topicSortIsTodoist ? [...expandedTodoistTopics, ...visible.filter((topic) => topic.closed)] : visible,
        topicSortIsTodoist ? { ...openSections, active: true, snoozed: true, drafts: true } : openSections,
        topicDraftAt, topicHasPendingOutbox,
      )
      : {
          unreadMessages: 0,
          unreadTopics: 0,
          repliedTopics: 0,
          finishedTopics: 0,
          runningTopics: 0,
          todoistTopics: 0,
          bookmarkTopics: 0,
        },
    [topicSortIsTodoist, expandedTodoistTopics, controlsReady, visible, openSections, topicDraftAt, topicHasPendingOutbox],
  )
  const statusFilterCounts = useMemo(
    () => controlsReady
      ? countTopicFiltersForExpandedSections(
        topicSortIsTodoist ? statusFilterCountRows.filter((topic) => topic.closed || unreadOnly || !collapsedTodoistSections.has(topic.todoist_section || 'Sync pending')) : statusFilterCountRows,
        topicSortIsTodoist ? { ...openSections, active: true, snoozed: true, drafts: true } : openSections,
        topicDraftAt, topicHasPendingOutbox,
      )
      : {
          unreadMessages: 0,
          unreadTopics: 0,
          repliedTopics: 0,
          finishedTopics: 0,
          runningTopics: 0,
          todoistTopics: 0,
          bookmarkTopics: 0,
        },
    [topicSortIsTodoist, unreadOnly, collapsedTodoistSections, controlsReady, statusFilterCountRows, openSections, topicDraftAt, topicHasPendingOutbox],
  )
  const xLinkSourceCounts = useMemo(
    () => controlsReady && isXLinksGroup
      ? countOpenXLinkSourceTopics(sourceModeBaseVisible)
      : {
          todoistTopics: 0,
          bookmarkTopics: 0,
        },
    [controlsReady, isXLinksGroup, sourceModeBaseVisible],
  )
  const totalUnread = filterCounts.unreadTopics
  const repliedCount = filterCounts.repliedTopics
  const finishedCount = statusFilterCounts.finishedTopics
  const runningCount = statusFilterCounts.runningTopics
  const todoistCount = xLinkSourceCounts.todoistTopics
  const bookmarkCount = xLinkSourceCounts.bookmarkTopics
  const xLinkSourceTotalCount = todoistCount + bookmarkCount

  useEffect(() => {
    if (!unreadOnly) return
    setOpenSections((current) => {
      const next = { ...current }
      let changed = false
      const ensureOpen = (section: SectionKey, count: number) => {
        if (count > 0 && !next[section]) {
          next[section] = true
          changed = true
        }
      }
      ensureOpen('drafts', sorted.drafts.length)
      ensureOpen('active', sorted.active.length)
      ensureOpen('snoozed', sorted.snoozed.length)
      ensureOpen('done', sorted.done.length)
      return changed ? next : current
    })
  }, [
    unreadOnly,
    sorted.drafts.length,
    sorted.active.length,
    sorted.snoozed.length,
    sorted.done.length,
  ])

  const searchResults = useMemo<Topic[] | null>(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return null
    const titleMatches: Topic[] = []
    const contentMatches: Topic[] = []
    for (const t of topicRows) {
      if (t.closed && !includeDone) continue
      if ((t.title || '').toLowerCase().includes(q)) {
        titleMatches.push(t)
      } else if ((t.last_message_preview || '').toLowerCase().includes(q)) {
        contentMatches.push(t)
      }
    }
    return [...titleMatches, ...contentMatches]
  }, [includeDone, searchQuery, topicRows])

  const headerTopicCount = useMemo(() => {
    if (searchResults !== null) return searchResults.length
    if (topicSortIsTodoist) return expandedTodoistTopics.length + (openSections.done ? sorted.done.length : 0)
    const visibleTopicCount = countExpandedTopicRows({
      drafts: sorted.drafts.length,
      active: sorted.active.length,
      snoozed: sorted.snoozed.length,
      done: sorted.done.length,
    }, openSections)
    return visibleTopicCount + (dashboardTopic ? 1 : 0)
  }, [dashboardTopic, openSections, searchResults, sorted, topicSortIsTodoist, expandedTodoistTopics])

  const listItems = useMemo<TopicListItem[]>(() => {
    if (searchResults !== null) {
      if (searchResults.length === 0) return [{ kind: 'empty-search' }]
      return searchResults.map((topic) => ({
        kind: 'topic' as const,
        topic,
        section: classify(topic),
      }))
    }
    const items: TopicListItem[] = []
    if (dashboardTopic) {
      items.push({
        kind: 'topic' as const,
        topic: dashboardTopic,
        section: classify(dashboardTopic),
      })
    }
    if (topicSortIsTodoist) {
      for (const section of todoistSections) {
        items.push({ kind: 'todoist-section', name: section.name, count: section.topics.length })
        if (unreadOnly || !collapsedTodoistSections.has(section.name)) {
          items.push(...section.topics.map((topic) => ({ kind: 'topic' as const, topic, section: classify(topic) })))
        }
      }
      if (todoistSections.length === 0) items.push({ kind: 'empty-active' })
    } else {
      if (sorted.drafts.length > 0) {
        items.push({
          kind: 'section',
          section: 'drafts',
          title: 'Drafts',
          count: sorted.drafts.length,
          tone: 'draft',
          icon: <PencilLine className="w-3 h-3" />,
        })
        if (openSections.drafts) {
          items.push(...sorted.drafts.map((topic) => ({
            kind: 'topic' as const,
            topic,
            section: classify(topic),
          })))
        }
      }
      items.push({
        kind: 'section',
        section: 'active',
        title: 'Active',
        count: sorted.active.length,
        tone: 'default',
      })
      if (openSections.active) {
        if (sorted.active.length > 0) {
          items.push(...sorted.active.map((topic) => ({
            kind: 'topic' as const,
            topic,
            section: 'active' as const,
          })))
        } else if (sorted.drafts.length === 0 && sorted.snoozed.length === 0) {
          items.push({ kind: 'empty-active' })
        }
      }
      items.push({
        kind: 'section',
        section: 'snoozed',
        title: 'Snoozed',
        count: sorted.snoozed.length,
        tone: 'amber',
        icon: <Clock className="w-3 h-3" />,
      })
      if (openSections.snoozed) {
        items.push(...sorted.snoozed.map((topic) => ({
          kind: 'topic' as const,
          topic,
          section: 'snoozed' as const,
        })))
      }
    }
    items.push({
      kind: 'section',
      section: 'done',
      title: 'Done',
      count: sorted.done.length,
      tone: 'muted',
    })
    if (openSections.done) {
      items.push(...sorted.done.map((topic) => ({
        kind: 'topic' as const,
        topic,
        section: 'done' as const,
      })))
      if (!includeOldDone && sorted.done.length > 0) {
        items.push({ kind: 'older-done', loading: isFetching })
      }
    }
    return items
  }, [dashboardTopic, includeOldDone, isFetching, openSections, sorted, searchResults, topicSortIsTodoist, todoistSections, collapsedTodoistSections, unreadOnly])

  const visibleTopicIds = useMemo(() => (
    listItems
      .filter((item): item is Extract<TopicListItem, { kind: 'topic' }> => item.kind === 'topic')
      .slice(0, 10)
      .map((item) => item.topic.id)
  ), [listItems])

  useEffect(() => {
    if (visibleTopicIds.length === 0) return
    const primaryId = window.setTimeout(() => {
      preloadThreadViewComponents()
      for (const topicId of visibleTopicIds.slice(0, 3)) {
        prefetchChat(chat.id, topicId, { chat: false, topics: false })
      }
    }, 0)
    const restId = window.setTimeout(() => {
      preloadThreadViewComponents()
      for (const topicId of visibleTopicIds.slice(3)) {
        prefetchChat(chat.id, topicId, { chat: false, topics: false })
      }
    }, 75)
    return () => {
      window.clearTimeout(primaryId)
      window.clearTimeout(restId)
    }
  }, [chat.id, prefetchChat, visibleTopicIds])

  const orderedTopicIdsForAdvance = useCallback((item: Extract<TopicListItem, { kind: 'topic' }>): string[] => {
    if (searchResults !== null) return searchResults.filter((topic) => !topic.closed).map((topic) => topic.id)
    if (topicSortIsTodoist && !item.topic.closed) return expandedTodoistTopics.map((topic) => topic.id)
    if (item.section === 'active') return sorted.active.map((topic) => topic.id)
    if (item.section === 'snoozed') return sorted.snoozed.map((topic) => topic.id)
    return sorted.done.map((topic) => topic.id)
  }, [searchResults, sorted, topicSortIsTodoist, expandedTodoistTopics])

  const renderListItem = useCallback((_index: number, item: TopicListItem) => {
    if (item.kind === 'todoist-section') {
      return <SectionHeader title={item.name} count={item.count} tone="default"
        open={unreadOnly || !collapsedTodoistSections.has(item.name)}
        onToggle={() => toggleTodoistSection(item.name)} />
    }
    if (item.kind === 'section') {
      return (
        <SectionHeader
          title={item.title}
          count={item.count}
          open={openSections[item.section]}
          onToggle={() => toggle(item.section)}
          tone={item.tone}
          icon={item.icon}
        />
      )
    }
    if (item.kind === 'empty-active') {
      return (
        <div className="px-3 py-4 text-xs text-stone-500 italic">
          Nothing active — all caught up.
        </div>
      )
    }
    if (item.kind === 'empty-search') {
      return (
        <div className="px-3 py-4 text-xs text-stone-500 italic">
          No topics match your search.
        </div>
      )
    }
    if (item.kind === 'older-done') {
      return (
        <button
          type="button"
          onClick={() => setShowOlderDone(true)}
          disabled={item.loading}
          className={clsx(
            'mx-3 my-2 w-[calc(100%-1.5rem)] rounded-lg border px-3 py-2 text-left text-xs font-medium transition-colors',
            item.loading
              ? 'border-stone-200 bg-stone-100 text-stone-400 dark:border-stone-800 dark:bg-stone-900 dark:text-stone-600'
              : 'border-stone-200 bg-white text-stone-600 hover:bg-stone-50 dark:border-stone-800 dark:bg-stone-900 dark:text-stone-300 dark:hover:bg-stone-800',
          )}
        >
          {item.loading ? 'Loading older Done…' : 'Show older Done'}
        </button>
      )
    }
    const t = item.topic
    const sourceTaskId = todoistTaskId(t)
    const todoistHierarchy = topicSortIsTodoist
      ? {
          depth: todoistHierarchyByTopicId.get(t.id)?.depth ?? 0,
          hasChildren: !!todoistHierarchyByTopicId.get(t.id)?.hasChildren,
          expanded: !sourceTaskId || !collapsedTodoistTaskIds.has(sourceTaskId),
          onToggleExpanded: sourceTaskId ? () => toggleTodoistTaskCollapsed(sourceTaskId) : undefined,
        }
      : undefined
    return (
      <GroupTopicRow
        chatId={chat.id}
        topic={t}
        draft={draftsByChat[draftTargetKey(chat.id, t.id)] || null}
        section={item.section}
        todoistHierarchy={todoistHierarchy}
        onOpen={() => {
          if (isCommitmentsDashboardTopic(chat.id, t)) {
            navigate('/commitments')
            return
          }
          saveTopicAdvanceContext({
            chatId: chat.id,
            topicId: t.id,
            section: searchResults !== null ? 'search' : item.section,
            orderedTopicIds: orderedTopicIdsForAdvance(item),
          })
          onOpenTopic(t.id)
        }}
        onSnoozePick={() => setSnoozeFor(t.id)}
        onMarkDone={markDone}
        onReopen={reopenDone}
        onBlockSource={blockSource}
        selectionMode={selectionMode}
        selected={selectedTopicIds.has(t.id)}
        canSelect={!t.closed && !isCommitmentsDashboardTopic(chat.id, t)}
        onSelect={() => selectTopic(t)}
        onToggleSelected={() => toggleSelectedTopic(t)}
      />
    )
  }, [
    blockSource,
    chat.id,
    collapsedTodoistTaskIds,
    collapsedTodoistSections,
    toggleTodoistSection,
    unreadOnly,
    draftsByChat,
    markDone,
    navigate,
    onOpenTopic,
    orderedTopicIdsForAdvance,
    openSections,
    reopenDone,
    searchResults,
    selectTopic,
    selectedTopicIds,
    selectionMode,
    toggle,
    todoistHierarchyByTopicId,
    toggleTodoistTaskCollapsed,
    toggleSelectedTopic,
    topicSortIsTodoist,
  ])

  const itemKey = useCallback((_index: number, item: TopicListItem) => {
    if (item.kind === 'todoist-section') return `todoist-section-${item.name}`
    if (item.kind === 'section') return `section-${item.section}`
    if (item.kind === 'empty-active') return 'empty-active'
    if (item.kind === 'empty-search') return 'empty-search'
    if (item.kind === 'older-done') return 'older-done'
    return `topic-${item.topic.id}`
  }, [])
  const needsVirtualList = listItems.length > DIRECT_TOPIC_RENDER_LIMIT
  useEffect(() => {
    if (!needsVirtualList) {
      setVirtualListReady(false)
      return
    }
    setVirtualListReady(false)
    let timeoutId: number | null = null
    const frame = window.requestAnimationFrame(() => {
      timeoutId = window.setTimeout(() => setVirtualListReady(true), 0)
    })
    return () => {
      window.cancelAnimationFrame(frame)
      if (timeoutId !== null) window.clearTimeout(timeoutId)
    }
  }, [chat.id, listItems.length, needsVirtualList, searchQuery])

  if (isLoading) {
    return <div className="p-6 text-stone-500 text-sm">Loading topics…</div>
  }
  if (error) {
    return <div className="p-6 text-red-600 text-sm">Failed to load topics: {String((error as Error).message)}</div>
  }

  const empty = topicRows.length === 0
  const filterGridClass = isXLinksGroup || isTodoistTasksGroup ? 'grid-cols-5' : 'grid-cols-4'
  const xlinkSourceOptions: Array<{
    mode: XLinkSourceMode
    label: string
    count: number
    title: string
  }> = [
    { mode: 'xlinks', label: 'Xlinks', count: todoistCount, title: 'Show Todoist X-link topics' },
    { mode: 'bookmarks', label: 'Bookmarks', count: bookmarkCount, title: 'Show X bookmark topics' },
    { mode: 'both', label: 'Both', count: xLinkSourceTotalCount, title: 'Show X-links and bookmarks' },
  ]
  const xlinkSourceOption = xlinkSourceOptions.find((option) => option.mode === xLinkSourceMode) ?? xlinkSourceOptions[2]

  return (
    <div className="h-full min-h-0 flex flex-col chat-pattern-bg">
      {searchOpen && (
        <div className="shrink-0 border-b border-stone-200 bg-white/80 px-3 dark:border-stone-800 dark:bg-stone-900/80">
          <IncludeDoneCheckbox checked={includeDone} onChange={setIncludeDone} />
        </div>
      )}
      <div className="bg-white/80 dark:bg-stone-900/80 backdrop-blur shrink-0 z-10 px-3 py-2 border-b border-stone-200/70 dark:border-stone-800/70">
        <div className="relative flex items-center gap-2">
          <Inbox className="w-4 h-4 text-stone-500" />
          <h2 className="text-sm font-semibold flex-1 min-w-0 truncate">
            {chat.display_name || chat.id}
            <span className="ml-2 text-xs text-stone-500 font-normal">
              {headerTopicCount} {headerTopicCount === 1 ? 'topic' : 'topics'}
            </span>
          </h2>
          {isCommitmentsGroup && !selectionMode && (
            <div className="flex h-8 shrink-0 items-center overflow-hidden rounded-lg border border-blue-200/80 bg-blue-50/80 text-xs font-semibold text-blue-800 dark:border-blue-500/40 dark:bg-blue-950/40 dark:text-blue-100">
              <button
                type="button"
                onClick={() => navigate('/commitments')}
                className="inline-flex h-full items-center gap-1 px-2 transition-colors hover:bg-blue-100 dark:hover:bg-blue-900/50"
                data-testid="commitments-dashboard-in-app"
                aria-label="Open commitments dashboard in AgentChat"
                title="Open commitments dashboard in AgentChat"
              >
                <ChevronRight className="h-3.5 w-3.5" />
                App
              </button>
              <button
                type="button"
                onClick={() => void openPublicDashboard('commitments')}
                className="inline-flex h-full items-center gap-1 border-l border-blue-200/80 px-2 transition-colors hover:bg-blue-100 dark:border-blue-500/40 dark:hover:bg-blue-900/50"
                data-testid="commitments-dashboard-permalink"
                aria-label="Open commitments dashboard in Chrome"
                title="Open commitments dashboard in Chrome"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Web
              </button>
            </div>
          )}
          {isEventsGroup && !selectionMode && (
            <div className="flex h-8 shrink-0 items-center overflow-hidden rounded-lg border border-teal-200/80 bg-teal-50/80 text-xs font-semibold text-teal-800 dark:border-teal-500/40 dark:bg-teal-950/40 dark:text-teal-100">
              <button
                type="button"
                onClick={() => navigate('/events')}
                className="inline-flex h-full items-center gap-1 px-2 transition-colors hover:bg-teal-100 dark:hover:bg-teal-900/50"
                data-testid="events-dashboard-in-app"
                aria-label="Open events dashboard in AgentChat"
                title="Open events dashboard in AgentChat"
              >
                <ChevronRight className="h-3.5 w-3.5" />
                App
              </button>
              <button
                type="button"
                onClick={() => void openPublicDashboard('events')}
                className="inline-flex h-full items-center gap-1 border-l border-teal-200/80 px-2 transition-colors hover:bg-teal-100 dark:border-teal-500/40 dark:hover:bg-teal-900/50"
                data-testid="events-dashboard-permalink"
                aria-label="Open events dashboard in Chrome"
                title="Open events dashboard in Chrome"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Web
              </button>
            </div>
          )}
          {isArchitectureDeepenGroup && !selectionMode && (
            <button
              type="button"
              onClick={() => navigate('/architecture-campaigns')}
              className="inline-flex h-8 shrink-0 items-center gap-1 rounded-lg border border-amber-200/80 bg-amber-50/80 px-2 text-xs font-semibold text-amber-900 transition-colors hover:bg-amber-100 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100 dark:hover:bg-amber-900/50"
              data-testid="architecture-campaigns-in-app"
              aria-label="Open architecture campaign controls"
              title="Open architecture campaign controls"
            >
              <ListTree className="h-3.5 w-3.5" />
              Campaign
            </button>
          )}
          {!selectionMode && (
            <div className="relative shrink-0">
              <button
                type="button"
                onClick={() => setTopicMenuOpen((open) => !open)}
                className="grid place-items-center w-8 h-8 rounded-lg text-stone-500 hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors"
                aria-label="Group topic actions"
                aria-expanded={topicMenuOpen}
              >
                <MoreVertical className="w-4 h-4" />
              </button>
              {topicMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setTopicMenuOpen(false)}
                  />
                  <ul className="absolute right-0 top-full z-40 mt-1 min-w-[160px] rounded-xl border border-stone-200 bg-white p-1 text-sm shadow-2xl dark:border-stone-700 dark:bg-stone-800">
                    <li>
                      <button
                        type="button"
                        onClick={openNewTopicDialog}
                        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-stone-700 hover:bg-stone-100 dark:text-stone-200 dark:hover:bg-stone-700"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        New topic
                      </button>
                    </li>
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
        {selectionMode ? (
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={clearSelection}
              className="grid place-items-center w-8 h-8 rounded-lg text-stone-500 hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors"
              aria-label="Clear selection"
            >
              <X className="w-4 h-4" />
            </button>
            <span className="flex-1 min-w-0 text-xs font-semibold text-stone-700 dark:text-stone-200">
              {selectedTopicIds.size} selected
            </span>
            <button
              type="button"
              onClick={() => void markReadSelectedTopics()}
              disabled={markChatRead.isPending}
              className={clsx(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors',
                markChatRead.isPending
                  ? 'bg-stone-200 text-stone-500 dark:bg-stone-800 dark:text-stone-400'
                  : 'bg-blue-600 text-white hover:bg-blue-700',
              )}
              aria-label="Mark selected topics as read"
            >
              <MailOpen className="w-3.5 h-3.5" />
              Mark Read
            </button>
            <button
              type="button"
              onClick={() => void closeSelectedTopics()}
              disabled={closeTopics.isPending}
              className={clsx(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors',
                closeTopics.isPending
                  ? 'bg-stone-200 text-stone-500 dark:bg-stone-800 dark:text-stone-400'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700',
              )}
              aria-label="Close selected topics"
            >
              <CheckSquare className="w-3.5 h-3.5" />
              Close
            </button>
          </div>
        ) : controlsReady ? (
          <div
            className={clsx('mt-2 grid w-full items-center gap-1', filterGridClass)}
            data-topic-filter-row
          >
            {/* Zero-count filters stay selectable so matching topics appear here
                automatically when their state changes. */}
            <button
              title={unreadOnly ? 'Show all topics' : 'Show only topics with unread messages'}
              aria-pressed={unreadOnly}
              aria-label="Unread topics filter"
              onClick={() => toggleTopicFilter('unreadOnly')}
              className={filterButtonClass(unreadOnly, 'bg-blue-600 text-white hover:bg-blue-700')}
            >
              Unread{totalUnread > 0 ? ` ${totalUnread}` : ''}
            </button>
            {/* Replied-only filter — show only topics where Mark has typed
                at least one message. Useful in auto-spawn groups (Daily
                Brief, Skill Tuning, X-Link, etc.) where most topics are
                agent-only and the engaged ones get lost in the firehose. */}
            <button
              title={repliedOnly ? 'Show all topics' : "Show only topics you've replied to"}
              aria-pressed={repliedOnly}
              aria-label="Replied topics filter"
              onClick={() => toggleTopicFilter('repliedOnly')}
              className={filterButtonClass(repliedOnly, 'bg-blue-600 text-white hover:bg-blue-700')}
            >
              Replied{repliedCount > 0 ? ` ${repliedCount}` : ''}
            </button>
            <button
              title={finishedOnly ? 'Show working and queued topics too' : 'Show only topics with no active or queued work'}
              aria-pressed={finishedOnly}
              aria-label="Finished topics filter"
              onClick={() => toggleTopicFilter('finishedOnly')}
              className={filterButtonClass(finishedOnly, 'bg-emerald-600 text-white hover:bg-emerald-700')}
            >
              Finished{finishedCount > 0 ? ` ${finishedCount}` : ''}
            </button>
            <button
              title={runningOnly ? 'Show idle and finished topics too' : 'Show only topics with active or queued work'}
              aria-pressed={runningOnly}
              aria-label="Running topics filter"
              onClick={() => toggleTopicFilter('runningOnly')}
              className={filterButtonClass(runningOnly, 'bg-blue-600 text-white hover:bg-blue-700')}
            >
              Running{runningCount > 0 ? ` ${runningCount}` : ''}
            </button>
            {isTodoistTasksGroup && (
              <button
                title={topicSortIsTodoist ? 'Use AgentChat topic order' : 'Use Todoist sections and task order'}
                aria-pressed={topicSortIsTodoist}
                aria-label="Todoist order sort"
                onClick={toggleTopicSortMode}
                className={filterButtonClass(topicSortIsTodoist, 'bg-red-600 text-white hover:bg-red-700')}
              >
                <ListTree className="h-3 w-3 shrink-0" aria-hidden />
                <span className="block min-w-0 truncate">Todoist</span>
              </button>
            )}
            {isXLinksGroup && (
              <button
                title={`${xlinkSourceOption.title}; tap to cycle`}
                aria-pressed={xLinkSourceMode !== 'both'}
                aria-label="X-link source filter"
                onClick={cycleXLinkSourceMode}
                className={filterButtonClass(xLinkSourceMode !== 'both', 'bg-red-600 text-white hover:bg-red-700')}
              >
                <span className="block min-w-0 truncate">
                  {xlinkSourceOption.label}{xlinkSourceOption.count > 0 ? ` ${xlinkSourceOption.count}` : ''}
                </span>
              </button>
            )}
          </div>
        ) : null}
      </div>
      <NewTopicPromptDialog
        open={newTopicOpen}
        pending={createTopic.isPending}
        error={newTopicError}
        initialPrompt={newTopicInitialPrompt}
        onCancel={() => {
          if (!createTopic.isPending) {
            setNewTopicOpen(false)
            setNewTopicInitialPrompt('')
            setNewTopicFocusComposerOnCreate(false)
          }
        }}
        onSubmit={createTopicFromPrompt}
      />

      {empty && !searchQuery && (
        <div className="grid h-[60vh] place-items-center px-6">
          <div className="text-center max-w-sm">
            <div className="text-4xl mb-3">📭</div>
            <div className="text-sm font-medium mb-1">No topics yet</div>
            <p className="text-xs text-stone-500">
              Topics in this group are auto-created when matching events arrive
              (e.g. a Todoist task or ntfy alert).
            </p>
          </div>
        </div>
      )}

      {(!empty || searchQuery) && (!needsVirtualList || !virtualListReady) && (
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          {(needsVirtualList ? listItems.slice(0, FIRST_FRAME_TOPIC_ROWS) : listItems).map((item, index) => (
            <div key={itemKey(index, item)}>
              {renderListItem(index, item)}
            </div>
          ))}
        </div>
      )}

      {(!empty || searchQuery) && needsVirtualList && virtualListReady && (
        <Virtuoso
          className="flex-1 min-h-0"
          data={listItems}
          itemContent={renderListItem}
          computeItemKey={itemKey}
          increaseViewportBy={{ top: 180, bottom: 360 }}
          style={{ overflowX: 'hidden' }}
        />
      )}

      {snoozeFor && (
        <SnoozePicker
          onCancel={() => setSnoozeFor(null)}
          onPick={(wakeAt) => {
            snoozeTopic.mutate({ topic_id: snoozeFor, wake_at: wakeAt })
            setSnoozeFor(null)
          }}
        />
      )}

      {undoToast && (
        <UndoToast
          message={undoToast.message}
          onUndo={async () => {
            await Promise.all(undoToast.topicIds.map((topicId) =>
              reopenTopic.mutateAsync({
                topic_id: topicId,
                reopenTodoist: undoToast.reopenTodoist,
              }),
            ))
            setUndoToast(null)
          }}
          onDismiss={() => setUndoToast(null)}
          durationMs={6000}
        />
      )}

      {(topicActionError || snoozeTopic.error) && (
        <div
          role="alert"
          className={clsx(
            'fixed left-1/2 -translate-x-1/2 z-50 max-w-sm w-[calc(100%-2rem)]',
            'bottom-[max(env(safe-area-inset-bottom,0px),16px)]',
            'rounded-xl bg-red-950 px-4 py-3 text-sm text-red-100 shadow-2xl',
          )}
          onClick={() => { setTopicActionError(null); snoozeTopic.reset() }}
        >
          {topicActionError || snoozeTopic.error?.message}
        </div>
      )}
    </div>
  )
}

function SectionHeader({
  title, count, open, onToggle, tone, icon,
}: {
  title: string
  count: number
  open: boolean
  onToggle: () => void
  tone: 'default' | 'amber' | 'muted' | 'draft'
  icon?: React.ReactNode
}) {
  const isDraft = tone === 'draft'
  return (
    <button
      onClick={onToggle}
      className={clsx(
        'w-full min-h-9 flex items-center gap-2 px-3 py-1.5',
        'border-b backdrop-blur',
        isDraft
          ? 'bg-amber-100/90 dark:bg-amber-900/40 border-amber-300/60 dark:border-amber-700/40 text-amber-900 dark:text-amber-200 font-bold'
          : 'bg-stone-50/95 dark:bg-stone-900/95 border-stone-200/50 dark:border-stone-800/50 font-semibold',
        'text-[13px]',
        !isDraft && (
          tone === 'amber' ? 'text-amber-800 dark:text-amber-400' :
          tone === 'muted' ? 'text-stone-600 dark:text-stone-400' :
            'text-stone-700 dark:text-stone-300'
        ),
      )}
    >
      {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      {icon}
      <span>{title}</span>
      <span className={clsx('font-normal', isDraft ? 'text-amber-700 dark:text-amber-300' : 'text-stone-500 dark:text-stone-400')}>
        {count}
      </span>
    </button>
  )
}
